// Country picker data. The server's CountryPolicy collection is the source
// of truth (GET /countries — every listed country with its derived
// open/locked flag); the hardcoded list below is an OFFLINE FALLBACK ONLY,
// mirroring the seed so a network hiccup can never blank the picker.
//
// `open` is derived server-side from payout-rail facts (no manual toggles):
// locked countries stay visible in the picker so a DJ can join the waitlist
// instead of hitting a dead end after money is already flowing.
import api from "../api";
import type { PayoutRail } from "../types/payout";

// Phase C: defaultCurrency is the country's own pay-in currency (what a new
// DJ's prices default to). Keep in sync with server SUPPORTED_PAYIN_CURRENCIES.
export type PayinCurrencyCode =
  | "usd" | "eur" | "gbp" | "cad" | "chf" | "sek" | "dkk" | "nok" | "pln" | "czk" | "huf" | "ron";

const PAYIN_SET = new Set<string>([
  "usd", "eur", "gbp", "cad", "chf", "sek", "dkk", "nok", "pln", "czk", "huf", "ron",
]);

export interface SupportedCountry {
  name: string;
  code: string; // ISO-3166 alpha-2
  flag: string;
  open: boolean; // sign-ups open (a working payout rail exists)
  defaultCurrency: PayinCurrencyCode;
  /** Who pays a talent there: Stripe, MassPay (Indonesia first), or nobody yet (locked). */
  rail: PayoutRail | null;
  /** MassPay countries: the wallets to name ("GoPay, OVO, DANA, LinkAja"). Empty elsewhere. */
  wallets: string[];
}

// Every open country in the offline mirror is a Stripe country; MassPay
// countries open only through the server (MASSPAY_ENABLED), so offline they
// stay locked, exactly like the server's own fallback.
const C = (name: string, code: string, flag: string, open: boolean, defaultCurrency: PayinCurrencyCode = "usd"): SupportedCountry =>
  ({ name, code, flag, open, defaultCurrency, rail: open ? "stripe" : null, wallets: [] });

// Mirrors server lib/countryPolicy.js FALLBACK_POLICIES (45 listed countries —
// Iceland is not a Stripe country and is hidden entirely).
export const FALLBACK_COUNTRIES: SupportedCountry[] = [
  C("United States", "US", "🇺🇸", true),
  C("Austria", "AT", "🇦🇹", true, "eur"),
  C("Belgium", "BE", "🇧🇪", true, "eur"),
  C("Bulgaria", "BG", "🇧🇬", true, "eur"),
  C("Croatia", "HR", "🇭🇷", true, "eur"),
  C("Cyprus", "CY", "🇨🇾", true, "eur"),
  C("Estonia", "EE", "🇪🇪", true, "eur"),
  C("Finland", "FI", "🇫🇮", true, "eur"),
  C("France", "FR", "🇫🇷", true, "eur"),
  C("Germany", "DE", "🇩🇪", true, "eur"),
  C("Greece", "GR", "🇬🇷", true, "eur"),
  C("Ireland", "IE", "🇮🇪", true, "eur"),
  C("Italy", "IT", "🇮🇹", true, "eur"),
  C("Latvia", "LV", "🇱🇻", true, "eur"),
  C("Lithuania", "LT", "🇱🇹", true, "eur"),
  C("Luxembourg", "LU", "🇱🇺", true, "eur"),
  C("Malta", "MT", "🇲🇹", true, "eur"),
  C("Netherlands", "NL", "🇳🇱", true, "eur"),
  C("Portugal", "PT", "🇵🇹", true, "eur"),
  C("Slovakia", "SK", "🇸🇰", true, "eur"),
  C("Slovenia", "SI", "🇸🇮", true, "eur"),
  C("Spain", "ES", "🇪🇸", true, "eur"),
  // Phase C: these price in their own currency now — fans pay it too.
  C("United Kingdom", "GB", "🇬🇧", true, "gbp"),
  C("Canada", "CA", "🇨🇦", true, "cad"),
  C("Gibraltar", "GI", "🇬🇮", true, "gbp"),
  // Phase B opened these; Phase C localized their pay-in. Mirrors
  // the server derivation; keep in sync with lib/countryPolicy.js.
  C("Switzerland", "CH", "🇨🇭", true, "chf"),
  C("Liechtenstein", "LI", "🇱🇮", true, "chf"),
  C("Sweden", "SE", "🇸🇪", true, "sek"),
  C("Denmark", "DK", "🇩🇰", true, "dkk"),
  C("Norway", "NO", "🇳🇴", true, "nok"),
  C("Poland", "PL", "🇵🇱", true, "pln"),
  C("Czechia", "CZ", "🇨🇿", true, "czk"),
  C("Hungary", "HU", "🇭🇺", true, "huf"),
  C("Romania", "RO", "🇷🇴", true, "ron"),
  // Locked until a payout rail exists — selectable, routes to the waitlist.
  C("Malaysia", "MY", "🇲🇾", false),
  C("India", "IN", "🇮🇳", false),
  C("United Arab Emirates", "AE", "🇦🇪", false),
  C("Australia", "AU", "🇦🇺", false),
  C("New Zealand", "NZ", "🇳🇿", false),
  C("Japan", "JP", "🇯🇵", false),
  C("Singapore", "SG", "🇸🇬", false),
  C("Hong Kong", "HK", "🇭🇰", false),
  C("Mexico", "MX", "🇲🇽", false),
  C("Brazil", "BR", "🇧🇷", false),
  // Paid through MassPay, not Stripe — opens with the server's MASSPAY_ENABLED.
  C("Indonesia", "ID", "🇮🇩", false),
];

// An older server sends no rail: every open country was a Stripe country then.
const readRail = (c: Partial<SupportedCountry>): PayoutRail | null =>
  c.rail === "stripe" || c.rail === "masspay" ? c.rail : c.open ? "stripe" : null;

let cached: SupportedCountry[] | null = null;
let cachedDetected: string | null = null;

export interface CountryData {
  countries: SupportedCountry[];
  /**
   * The country the talent's connection is in, when the list has it (server
   * lib/geo/ipCountry, never stored) — the picker pre-selects it (1.5.2).
   * null offline, on an older server, or when unknown.
   */
  detectedCode: string | null;
}

/**
 * Live country list from the server, cached for the session. Any failure
 * (offline, server down, bad payload) falls back to the hardcoded mirror —
 * the picker must always render.
 *
 * `fresh`: skip the cache. The answer can depend on who asks (a signed-in
 * talent on the MassPay pilot list sees Indonesia open), so a list fetched
 * before sign-in must not answer for the signed-in talent.
 *
 * `masspay=1` tells the server this app has MassPay's screens: MassPay
 * countries come back open (app 1.5.1, which has none, sees them as "not
 * yet"). A–Z, like every country picker (194 countries since 2026-10-16).
 */
export async function fetchSupportedCountries({ fresh = false }: { fresh?: boolean } = {}): Promise<SupportedCountry[]> {
  return (await fetchCountryData({ fresh })).countries;
}

/** The list plus the country the talent's connection is in (the picker's suggestion). */
export async function fetchCountryData({ fresh = false }: { fresh?: boolean } = {}): Promise<CountryData> {
  if (cached && !fresh) return { countries: cached, detectedCode: cachedDetected };
  try {
    const { data } = await api.get<{ countries: SupportedCountry[]; detectedCode?: unknown }>("/countries", { params: { masspay: 1 } });
    if (Array.isArray(data?.countries) && data.countries.length > 0) {
      cached = byName(data.countries.map((c) => ({
        name: c.name,
        code: c.code,
        flag: c.flag,
        open: !!c.open,
        // Phase C: accept any live pay-in currency from the server; anything
        // unknown (future additions on a stale bundle) degrades to usd.
        defaultCurrency: (PAYIN_SET.has(String(c.defaultCurrency)) ? c.defaultCurrency : "usd") as PayinCurrencyCode,
        rail: c.open ? readRail(c) : null,
        wallets: Array.isArray(c.wallets) ? c.wallets.filter((w): w is string => typeof w === "string" && !!w.trim()) : [],
      })));
      const code = typeof data.detectedCode === "string" ? data.detectedCode.toUpperCase() : "";
      cachedDetected = cached.some((c) => c.code === code) ? code : null;
      return { countries: cached, detectedCode: cachedDetected };
    }
  } catch {
    // fall through to the offline mirror
  }
  return { countries: byName(FALLBACK_COUNTRIES), detectedCode: null };
}

const byName = (list: SupportedCountry[]): SupportedCountry[] =>
  [...list].sort((a, b) => a.name.localeCompare(b.name, "en"));
